import React, { useState, useRef, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { PDFDownloadLink } from "@react-pdf/renderer";
import Bill from "./Bill";
import axios from "axios";
import { toast } from "react-toastify";

const AddClient = ({ onClose }) => {
  const [plan, setPlan] = useState("1");
  const [trainingType, setTrainingType] = useState("General");
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [trainers, setTrainers] = useState([]);
  const [logoImage, setLogoImage] = useState();
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date());
  const [customEndDate, setCustomEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [amountPaid, setAmountPaid] = useState(0);
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [clientDetails, setClientDetails] = useState({
    name: "",
    phonenumber: "",
    email: "",
    height: 0,
    weight: 0,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPDF, setShowPDF] = useState(false);
  const [finalClientId, setFinalClientId] = useState("");
  const [paymentmethod, setpaymentmethod] = useState("");

  const fetchTrainers = async () => {
    try {
      const response = await axios.get(
        "https://klimkaara-backend-pgnq.onrender.com/trainers/getTrainers"
      );
      setTrainers(response.data.trainers);
      if (response.data.length > 0) setSelectedTrainer(response.data[0].name);
    } catch (error) {
      console.error("Error fetching trainers:", error);
    }
  };

  useEffect(() => {
    fetchTrainers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsGenerating(true);

    let trainerId = "";
    if (trainingType === "Personal") {
      const trainer = trainers.find(
        (trainer) => trainer.name === selectedTrainer
      );
      trainerId = trainer ? trainer.trainerId : "";
    }

    const start = new Date(startDate);
    let end;
    
    if (plan === "None") {
      end = new Date(customEndDate);
    } else {
      end = new Date(start);
      end.setMonth(end.getMonth() + parseInt(plan, 10));
    }
    
    // Format endDate
    const endDay = String(end.getDate()).padStart(2, "0");
    const endMonth = String(end.getMonth() + 1).padStart(2, "0");
    const endYear = end.getFullYear();
    const endDateString = `${endDay}/${endMonth}/${endYear}`;
    
    // Format startDate
    const startDay = String(start.getDate()).padStart(2, "0");
    const startMonth = String(start.getMonth() + 1).padStart(2, "0");
    const startYear = start.getFullYear();
    const startDateString = `${startDay}/${startMonth}/${startYear}`;
    try {
      const response = await axios.post(
        "https://klimkaara-backend-pgnq.onrender.com/clients/create",
        {
          ...clientDetails,
          plan: plan === "None" ? "None" : parseInt(plan, 10),
          category: trainingType,
          trainerId,
          start,
          end,
          paymentmethod,
          balanceAmount,
        }
      );

      if (response.status === 200) {
        setLogoImage(response.data.base64);
        setFinalClientId(response.data.newClient.clientId.split("-")[1].replace(/^0+/, ''));
        setStartDate(startDateString);
        setEndDate(endDateString);
        toast.success("Client added successfully");
        setIsGenerating(false);
        setShowPDF(true);
      } else {
        throw new Error("Failed to add client");
      }
    } catch (error) {
      console.error("Error adding client:", error);
      setIsGenerating(false);
      
      // Show specific error message based on the error response
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.status === 400) {
        toast.error("Invalid client data. Please check all fields.");
      } else if (error.response?.status === 500) {
        toast.error("Server error. Please try again later.");
      } else if (error.message) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error("Error adding client. Please try again.");
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-lg relative max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-yellow-400">
            Add New Client
          </h2>
          <FaTimes
            className="text-gray-500 cursor-pointer hover:text-white transition-colors"
            onClick={onClose}
            size={20}
          />
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block">
                <span className="text-yellow-400 font-medium mb-2 block">Name</span>
                <input
                  type="text"
                  placeholder="Enter client name"
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  value={clientDetails.name}
                  onChange={(e) =>
                    setClientDetails({ ...clientDetails, name: e.target.value })
                  }
                />
              </label>
            </div>

            <div>
              <label className="block">
                <span className="text-yellow-400 font-medium mb-2 block">Type of Plan</span>
                <select
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                >
                  <option value="1">1 Month</option>
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="12">1 Year</option>
                  <option value="None">None</option>
                </select>
              </label>
            </div>

            <div>
              <label className="block">
                <span className="text-yellow-400 font-medium mb-2 block">Start Date</span>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
            </div>

            {plan === "None" && (
              <div>
                <label className="block">
                  <span className="text-yellow-400 font-medium mb-2 block">End Date</span>
                  <input
                    type="date"
                    min={startDate}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                  />
                </label>
              </div>
            )}

            <div>
              <label className="block">
                <span className="text-yellow-400 font-medium mb-2 block">Type of Training</span>
                <select
                  value={trainingType}
                  onChange={(e) => setTrainingType(e.target.value)}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                >
                  <option value="General">General Training</option>
                  <option value="Personal">Personal Training</option>
                </select>
              </label>
            </div>

            {trainingType === "Personal" && (
              <div>
                <label className="block">
                  <span className="text-yellow-400 font-medium mb-2 block">Trainer</span>
                  <select
                    value={selectedTrainer}
                    onChange={(e) => setSelectedTrainer(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  >
                    <option value="" disabled>Select a trainer</option>
                    {trainers.map((trainer) => (
                      <option key={trainer.name} value={trainer.name}>
                        {trainer.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <div>
              <label className="block">
                <span className="text-yellow-400 font-medium mb-2 block">Phone Number</span>
                <input
                  type="text"
                  placeholder="Enter phone number"
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  value={clientDetails.phonenumber}
                  onChange={(e) =>
                    setClientDetails({
                      ...clientDetails,
                      phonenumber: e.target.value,
                    })
                  }
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block">
                  <span className="text-yellow-400 font-medium mb-2 block">Height (cms)</span>
                  <input
                    type="number"
                    placeholder="Height"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                    value={clientDetails.height}
                    onChange={(e) =>
                      setClientDetails({
                        ...clientDetails,
                        height: e.target.valueAsNumber,
                      })
                    }
                    step="1"
                  />
                </label>
              </div>

              <div>
                <label className="block">
                  <span className="text-yellow-400 font-medium mb-2 block">Weight (kgs)</span>
                  <input
                    type="number"
                    placeholder="Weight"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                    value={clientDetails.weight}
                    onChange={(e) =>
                      setClientDetails({
                        ...clientDetails,
                        weight: e.target.valueAsNumber,
                      })
                    }
                    step="1"
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="block">
                <span className="text-yellow-400 font-medium mb-2 block">Email ID</span>
                <input
                  type="email"
                  placeholder="Enter email address"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  value={clientDetails.email}
                  onChange={(e) =>
                    setClientDetails({ ...clientDetails, email: e.target.value })
                  }
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block">
                  <span className="text-yellow-400 font-medium mb-2 block">Payment Method</span>
                  <select
                    value={paymentmethod}
                    onChange={(e) => setpaymentmethod(e.target.value)}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  >
                    <option value="">Select method</option>
                    <option value="Scan">Scan</option>
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                  </select>
                </label>
              </div>

              <div>
                <label className="block">
                  <span className="text-yellow-400 font-medium mb-2 block">Amount Paid</span>
                  <input
                    type="text"
                    placeholder="Enter amount"
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                    value={amountPaid}
                    onChange={(e) =>
                      setAmountPaid(e.target.value)
                    }
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="block">
                <span className="text-yellow-400 font-medium mb-2 block">Balance Amount</span>
                <input
                  type="text"
                  placeholder="Enter balance amount"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  value={balanceAmount}
                  onChange={(e) =>
                    setBalanceAmount(e.target.value)
                  }
                />
              </label>
            </div>

            <button
              type="submit"
              className="w-full bg-yellow-400 text-black font-bold p-3 rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </div>
              ) : (
                "Add Client"
              )}
            </button>
          </form>
        </div>

        {showPDF && (
          <div className="border-t border-gray-700 p-6">
            <PDFDownloadLink
              document={
                <Bill
                  clientDetails={clientDetails}
                  trainingType={trainingType}
                  plan={plan}
                  selectedTrainer={selectedTrainer}
                  logoImage={logoImage}
                  finalClientId={finalClientId}
                  startDate={startDate}
                  endDate={endDate}
                  paymentmethod={paymentmethod}
                  amountPaid={amountPaid}
                  balanceAmount={balanceAmount}
                />
              }
              fileName={`${clientDetails.name}_Bill.pdf`}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg text-center block transition-colors"
            >
               {({ blob, url, loading, error }) =>
                loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating PDF...
                  </div>
                ) : (
                  "Download PDF"
                )
              }
            </PDFDownloadLink>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddClient;
